import React from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import {
  PropertyExternalMediaMatterportVirtualTourInput as MatterportExternalMedia,
  PropertyExternalMediaYoutubeVideoInput as YoutubeExternalMedia,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Label from 'src/modules/shared/components/atoms/inputs/label';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type {
  PropertyFormInputValues,
  PropertyFormValues,
} from '../../validations/property-validations';

interface PropertyExternalMediaFieldsProps {
  /** React Hook Form control for the property form. */
  control: Control<PropertyFormInputValues, unknown, PropertyFormValues>;
  /** Current property form validation errors. */
  errors?: FieldErrors<PropertyFormInputValues>;
  /** Whether form controls should be disabled while saving. */
  loading: boolean;
  /** Registers external-media URL inputs with the property form. */
  register: UseFormRegister<PropertyFormInputValues>;
  /** Updates discriminated external-media fields when the provider changes. */
  setValue: UseFormSetValue<PropertyFormInputValues>;
}

const youtubeProviderValue: string = YoutubeExternalMedia.provider.YOUTUBE;
const matterportProviderValue: string =
  MatterportExternalMedia.provider.MATTERPORT;

/**
 * Edits the ordered externally hosted media collection for a property.
 */
export const PropertyExternalMediaFields: React.FC<
  PropertyExternalMediaFieldsProps
> = ({ control, errors, loading, register, setValue }) => {
  const { t } = useI18n();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'externalMedia',
  });
  const externalMedia = useWatch({ control, name: 'externalMedia' });
  const providerOptions = [
    {
      value: YoutubeExternalMedia.provider.YOUTUBE,
      content: t('catalog.properties.form.externalMedia.provider.youtube'),
    },
    {
      value: MatterportExternalMedia.provider.MATTERPORT,
      content: t('catalog.properties.form.externalMedia.provider.matterport'),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div>
        <Label size="md">
          {t('catalog.properties.form.externalMedia.title')}
        </Label>
        <Text size="tiny" className="mt-1 text-slate-500">
          {t('catalog.properties.form.externalMedia.help')}
        </Text>
      </div>

      {fields.map((field, index) => (
        <div
          key={field.id}
          className="grid grid-cols-1 items-start gap-2 rounded-md border border-neutral-200 p-3 md:grid-cols-[12rem_1fr_auto]"
        >
          <Controller
            control={control}
            name={`externalMedia.${index}.provider`}
            render={({ field: { name, onBlur, onChange, value } }) => (
              <Select
                aria-label={t(
                  'catalog.properties.form.externalMedia.providerLabel',
                  { position: index + 1 }
                )}
                disabled={loading}
                name={name}
                onBlur={onBlur}
                options={providerOptions}
                value={value}
                onChange={(event) => {
                  const provider = event.target.value;
                  if (
                    provider !== youtubeProviderValue &&
                    provider !== matterportProviderValue
                  ) {
                    return;
                  }
                  onChange(provider);
                  setValue(
                    `externalMedia.${index}.kind`,
                    provider === matterportProviderValue
                      ? MatterportExternalMedia.kind.VIRTUAL_TOUR
                      : YoutubeExternalMedia.kind.VIDEO,
                    { shouldDirty: true }
                  );
                }}
              />
            )}
          />
          <TextInput
            {...register(`externalMedia.${index}.url`)}
            aria-label={t('catalog.properties.form.externalMedia.urlLabel', {
              position: index + 1,
            })}
            disabled={loading}
            error={errors?.externalMedia?.[index]?.url?.message}
            placeholder={
              externalMedia?.[index]?.provider ===
              MatterportExternalMedia.provider.MATTERPORT
                ? 'https://my.matterport.com/show/?m=…'
                : 'https://www.youtube.com/watch?v=…'
            }
          />
          <Button
            type="button"
            size="sm"
            appearance="ghost"
            variant="danger"
            disabled={loading}
            onClick={() => remove(index)}
          >
            {t('catalog.properties.form.externalMedia.remove')}
          </Button>
        </div>
      ))}

      <div>
        <Button
          type="button"
          size="sm"
          appearance="outline"
          variant="secondary"
          disabled={loading}
          onClick={() =>
            append({
              kind: YoutubeExternalMedia.kind.VIDEO,
              provider: YoutubeExternalMedia.provider.YOUTUBE,
              url: '',
            })
          }
        >
          {t('catalog.properties.form.externalMedia.add')}
        </Button>
      </div>
    </div>
  );
};
